import { Agent, Contributor, Year } from "@biblioteksentralen/cordata";
import { PickFromExtracted } from "@libry-content/types";
import { sort } from "radash";
import { getPath } from "@libry-content/common";
import { isPerson } from "../types";
import { Translate } from "../../utils/hooks/useTranslation";

export const addAgentHrefs = (contributors: Contributor[] | undefined) =>
  contributors?.map(({ agent, ...rest }) => ({ ...rest, agent: { ...agent, href: getAgentPath(agent) } }));

export const getAgentDatesString = (
  agent: PickFromExtracted<Agent, { type: "Person" }, "type" | "unspecifiedDate", "type">,
  t: Translate["t"]
): string | undefined => {
  if (!isPerson(agent)) return undefined;
  if (agent.birth && agent.death) {
    const includeDesignation = agent.birth.year < 0;
    const parsedBirthYear = getParsedYear({ year: agent.birth, t, includeDesignation, includeApproximation: true });
    const parsedDeathYear = getParsedYear({ year: agent.death, t, includeDesignation, includeApproximation: true });
    return `${parsedBirthYear} - ${parsedDeathYear}`;
  }
  if (agent.birth)
    return getParsedYear({
      year: agent.birth,
      t,
      includeDesignation: agent.birth.year < 0,
      includeApproximation: true,
    });
  if (agent.death)
    return getParsedYear({
      year: agent.death,
      t,
      includeDesignation: agent.death.year < 0,
      includeApproximation: true,
    });
  if (agent.activeYears)
    return `~${getParsedYear({ year: agent.activeYears, t, includeDesignation: agent.activeYears.year < 0 })}`;
  return agent.unspecifiedDate?.replace(/ ?- ?/, " - ");
};

type ParsedYearProps = {
  year: Year;
  t: Translate["t"];
  includeDesignation?: boolean;
  includeApproximation?: boolean;
};

const getParsedYear = ({ year, t, includeDesignation, includeApproximation }: ParsedYearProps) =>
  `${includeApproximation && year.isApproximate ? t("ca. ") : ""}${Math.abs(year.year)}${
    includeDesignation ? ` ${getDesignation(year.year, t)}.` : ""
  }`;

const getDesignation = (year: number, t: Translate["t"]) => (year >= 0 ? t("evt") : t("fvt"));

export const orderByMainContributor = (contributors: Contributor[]): Contributor[] =>
  sort(contributors, ({ isMainContributor }) => (isMainContributor ? 2 : 1));

export const getAgentPath = (agent: Pick<Agent, "_origin" | "id" | "type" | "name" | "identifiers">) =>
  agent._origin?.name === "promus" ? getPath(agent) : undefined;
